import React, { useMemo } from "react";
import * as Md from "react-icons/md";

const DEFAULT_PAGE_SIZE = 10;

/** Build a compact page list with ellipses: 1 … 4 5 6 … 20 */
function buildPageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items = new Set([1, total, current, current - 1, current + 1]);
  if (current <= 3) {
    items.add(2);
    items.add(3);
    items.add(4);
  }
  if (current >= total - 2) {
    items.add(total - 1);
    items.add(total - 2);
    items.add(total - 3);
  }

  const sorted = [...items].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("…");
    out.push(sorted[i]);
  }
  return out;
}

export function paginateList(list, page, pageSize = DEFAULT_PAGE_SIZE) {
  const items = Array.isArray(list) ? list : [];
  const size = Math.max(1, pageSize);
  const totalPages = Math.max(1, Math.ceil(items.length / size));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * size;
  return {
    items: items.slice(start, start + size),
    page: safePage,
    pageSize: size,
    total: items.length,
    totalPages,
    start: items.length === 0 ? 0 : start + 1,
    end: Math.min(start + size, items.length),
  };
}

export default function TablePagination({
  page,
  pageSize = DEFAULT_PAGE_SIZE,
  total,
  onPageChange,
  className = "",
}) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / Math.max(1, pageSize)));
  const safePage = Math.min(Math.max(1, page || 1), totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, total || 0);

  const pageItems = useMemo(
    () => buildPageItems(safePage, totalPages),
    [safePage, totalPages]
  );

  if (!total || total <= pageSize) {
    return null;
  }

  return (
    <div className={`tbl-pagination ${className}`.trim()}>
      <span className="tbl-pagination__summary">
        Showing {start}–{end} of {total}
      </span>
      <nav className="tbl-pagination__nav" aria-label="Table pagination">
        <button
          type="button"
          className="tbl-pagination__btn"
          aria-label="Previous page"
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
        >
          <Md.MdChevronLeft />
        </button>
        {pageItems.map((item, idx) =>
          item === "…" ? (
            <span key={`e-${idx}`} className="tbl-pagination__ellipsis" aria-hidden>
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className={
                item === safePage
                  ? "tbl-pagination__btn is-active"
                  : "tbl-pagination__btn"
              }
              aria-label={`Page ${item}`}
              aria-current={item === safePage ? "page" : undefined}
              onClick={() => onPageChange(item)}
            >
              {item}
            </button>
          )
        )}
        <button
          type="button"
          className="tbl-pagination__btn"
          aria-label="Next page"
          disabled={safePage >= totalPages}
          onClick={() => onPageChange(safePage + 1)}
        >
          <Md.MdChevronRight />
        </button>
      </nav>
    </div>
  );
}

export { DEFAULT_PAGE_SIZE };
